import React, { useState } from "react";
import MediaGrid from "../components/MediaGrid";
import useZoomModal from "../hooks/useZoomModal";
import {
  IconComment,
  IconLike,
  IconViews,
  IconShare,
  IconRepost,
  IconMore,
  IconSupport,
} from "../components/FeedIcons";
import { formatPostTime } from "../utils/formatPostTime";

/* 🔗 LINKIFY — makes links in text blue + clickable.
   Matches https://…, http://…, www.… and bare domains like coins.metisel.com */
const LINK_REGEX = /(?:https?:\/\/|www\.)[^\s<]+|(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+(?:com|net|org|io|co|app|dev|xyz|info|me|ai|gh|uk|us|ng|edu|gov|tv|site|online|store|tech|biz|ly|to|cc)\b(?::\d{2,5})?(?:[/?#][^\s<]*)?/gi;

const splitLinks = (text) => {
  const out = [];
  let last = 0;
  let m;
  LINK_REGEX.lastIndex = 0;
  while ((m = LINK_REGEX.exec(text))) {
    const start = m.index;
    const isBare = !/^(?:https?:\/\/|www\.)/i.test(m[0]);
    // bare domain glued to an email / word (user@site.com) → not a link
    if (isBare && start > 0 && /[@\w.-]/.test(text[start - 1])) continue;
    // keep trailing punctuation out of the link
    const url = m[0].replace(/[.,;:!?)\]}'"]+$/, "");
    if (!url) continue;
    if (start > last) out.push({ text: text.slice(last, start) });
    out.push({ url });
    last = start + url.length;
    LINK_REGEX.lastIndex = last;
  }
  if (last < text.length) out.push({ text: text.slice(last) });
  return out;
};

const LinkifiedText = ({ text }) => {
  if (!text) return null;

  return splitLinks(String(text)).map((part, i) => {
    if (!part.url) return <React.Fragment key={i}>{part.text}</React.Fragment>;

    const href = /^https?:\/\//i.test(part.url) ? part.url : `https://${part.url}`;
    return (
      <a
        key={i}
        href={href}
        target="_blank"
        rel="noopener noreferrer nofollow"
        onClick={(e) => e.stopPropagation()} // don't trigger the parent's click
        className="text-blue-500 hover:underline break-all"
      >
        {part.url}
      </a>
    );
  });
};

export default function PostViewCard({
  post,
  displayPost,
  isDeleted,
  isEdited,
  highlightId,
  isPureRepost,
  authorAvatar,
  headerVisible = true,
  navigate,
  closeAllReplies,
  setQuoteTarget,
  setIsReplyingToPost,
  setRepostMenu,
  setMenu,
  onLikePost,
  myChatId,
  users,
  onSupportPost,    // ← ({ post }) => void
  onViewEarnings,   // ← ({ post }) => void
    onOpenUser,
}) {
  const isHighlighted = highlightId === `post-card-${post.postId}`;
  const isAuthor = String(displayPost.author?.chatId) === String(myChatId);

  const [zoomOpen, setZoomOpen] = useState(false);
  const [zoomSrc, setZoomSrc] = useState(null);
  const [zoomType, setZoomType] = useState("image");

  const zoomModal = useZoomModal({ zoomOpen, setZoomOpen, zoomSrc, zoomType });

  const handleZoomMedia = (m) => {
    setZoomSrc(m.url);
    setZoomType(m.type);
    setZoomOpen(true);
  };

  const fmt = (n) =>
    !n ? null : n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n);

  const handleSupport = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isAuthor) {
      onViewEarnings?.({ post: displayPost });
    } else {
      onSupportPost?.({ post: displayPost });
    }
  };

  const showSupport =
    (isAuthor && onViewEarnings) ||
    (!isAuthor && onSupportPost && myChatId);

  return (
    <>
            {/* ── STICKY HEADER ── */}
      <div
        className={[
          "sticky top-0 z-20",
          "transition-transform duration-300 ease-in-out",
          headerVisible ? "translate-y-0" : "-translate-y-full",
          "bg-white/95 dark:bg-neutral-900/95 backdrop-blur-sm",
          "border-b border-neutral-200 dark:border-neutral-700",
          "px-4 flex items-center gap-3",
        ].join(" ")}
        style={{
          paddingTop: "calc(env(safe-area-inset-top, 0px) + 12px)",
          paddingBottom: "12px",
        }}
      >
            <button
  onClick={() => navigate(".", { replace: true, state: null })}
  className="flex items-center justify-center w-9 h-9 shrink-0 rounded-full text-neutral-700 dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-800 active:opacity-60 touch-manipulation transition-colors"
  aria-label="Back"
>
  <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
    <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
  </svg>
</button>
        <div className="font-semibold text-neutral-900 dark:text-neutral-100">Post</div>
      </div>

      {/* ── POST CARD ── */}
      <div
        id={`post-card-${post.postId}`}
        className={[
       "px-3 py-3 sm:px-4 sm:py-4 border-b border-neutral-200 dark:border-neutral-700",
          "scroll-mt-28 transition-all duration-500",
          isHighlighted
            ? "ring-2 ring-blue-500 ring-inset bg-blue-50 dark:bg-blue-950/20"
            : "",
        ].join(" ")}
      >
               {/* PURE REPOST LABEL */}
        {isPureRepost && (
          <div className="text-xs text-neutral-500 dark:text-neutral-400 mb-2 flex items-center gap-1">
            <span>🔁</span>
            <span>Repost</span>
          </div>
        )}

        {/* PURE COMMENT-REPOST LABEL */}
        {post?.isCommentRepost && !post?.isQuote && (
          <div className="text-xs text-neutral-500 dark:text-neutral-400 mb-2 flex items-center gap-1">
            <span>🔁</span>
            <span>Reposted a comment</span>
          </div>
        )}

        <div className="flex gap-3">
          {/* AVATAR */}
         
<div
  className="shrink-0 cursor-pointer"
  onClick={(e) => {
    e.stopPropagation();
    onOpenUser?.(displayPost.author?.chatId);
  }}
>
  {authorAvatar ? (
    <img
      src={authorAvatar}
      className="w-10 h-10 rounded-full object-cover"
      alt=""
    />
  ) : (
    <div className="w-10 h-10 rounded-full bg-neutral-300 dark:bg-neutral-700 flex items-center justify-center text-sm">
      👤
    </div>
  )}
</div>
          {/* BODY */}
          <div className="flex-1 min-w-0">
            {/* META */}
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
      <div className="font-semibold text-neutral-900 dark:text-neutral-100 truncate text-sm">
  <span
    onClick={(e) => {
      e.stopPropagation();
      onOpenUser?.(displayPost.author?.chatId);
    }}
    className="cursor-pointer hover:underline inline"
  >
    {displayPost.author?.displayName || displayPost.author?.username}
  </span>
</div>

<div className="text-sm text-neutral-400 flex items-center gap-1 flex-wrap">
  <span
    onClick={(e) => {
      e.stopPropagation();
      onOpenUser?.(displayPost.author?.chatId);
    }}
    className="truncate cursor-pointer hover:underline"
  >
    @{displayPost.author?.username}
  </span>

  <span>·</span>

  <span className="whitespace-nowrap">
    {formatPostTime(post.createdAt, "absolute")}
  </span>

  {isEdited && !isDeleted && (
    <span className="text-xs text-neutral-400">(edited)</span>
  )}
</div>
              </div>

              {/* 3-DOT */}
              {!isDeleted && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setMenu({
                      open: true,
                      post: displayPost,
                      type: isAuthor ? "post-actions" : "post-report",
                    });
                  }}
                  className="shrink-0 flex items-center justify-center rounded-full text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 active:scale-95 transition-all touch-manipulation"
                  style={{ minWidth: 36, minHeight: 36 }}
                  aria-label="More options"
                >
                  <IconMore />
                </button>
              )}
            </div>

            {/* TEXT */}
            {displayPost.text && (
              <div
                id={`post-body-${displayPost.postId}`}
                       className="mt-0.5 scroll-mt-28"
              >
                {isDeleted ? (
                  <p className="italic text-neutral-400 text-sm">
                    This post was deleted
                  </p>
                ) : (
                  <p className="whitespace-pre-wrap break-words text-[15px] leading-relaxed text-neutral-900 dark:text-neutral-100">
                    <LinkifiedText text={displayPost.text} />
                  </p>
                )}
              </div>
            )}

            {/* MEDIA */}
            {displayPost.media?.length > 0 && (
              <div className="mt-3">
                <MediaGrid media={displayPost.media} onClick={handleZoomMedia} />
              </div>
            )}

                      {/* QUOTED POST */}
            {post?.isQuote && post?.originalPost && (
              <div className="mt-3 rounded-2xl border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800/60 p-3">
                <div className="text-xs text-neutral-500 dark:text-neutral-400 mb-1.5">✏️ Quoted</div>
              <div className="font-semibold text-sm text-neutral-800 dark:text-neutral-200">
  {post.originalPost.author?.displayName || post.originalPost.author?.username}
</div>
                {post.originalPost.text && (
                  <div className="text-sm mt-1 whitespace-pre-wrap break-words text-neutral-700 dark:text-neutral-300">
                    <LinkifiedText text={post.originalPost.text} />
                  </div>
                )}
                {post.originalPost.media?.length > 0 && (
                  <div className="mt-2">
                    <MediaGrid media={post.originalPost.media} onClick={handleZoomMedia} />
                  </div>
                )}
              </div>
            )}

            {/* QUOTED / REPOSTED COMMENT — ✅ NEW: this whole block was missing */}
            {post?.isCommentRepost && post?.originalComment && (
              <div className="mt-3 rounded-2xl border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800/60 p-3">
                <div className="text-xs text-neutral-500 dark:text-neutral-400 mb-1.5">
                  {post.isQuote ? "✏️ Quoted comment" : "🔁 Reposted comment"}
                </div>
                <div className="font-semibold text-sm text-neutral-800 dark:text-neutral-200">
                  {post.originalComment.author?.displayName || post.originalComment.author?.username}
                </div>
                {post.originalComment.text && (
                  <div className="text-sm mt-1 whitespace-pre-wrap break-words text-neutral-700 dark:text-neutral-300">
                    <LinkifiedText text={post.originalComment.text} />
                  </div>
                )}
                {post.originalComment.media?.length > 0 && (
                  <div className="mt-2">
                    <MediaGrid media={post.originalComment.media} onClick={handleZoomMedia} />
                  </div>
                )}
              </div>
            )}

            {/* ── ACTION BAR ── */}
                       <div
                       className="-mt-2 flex items-center text-neutral-500 dark:text-neutral-400"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
            >
              {/* LEFT CLUSTER — evenly distributed */}
              <div className="flex items-center justify-between flex-1 pr-2">

                {/* COMMENT */}
                <button
                  onClick={() => {
                    closeAllReplies();
                    setIsReplyingToPost(true);
                  }}
                  className="flex items-center gap-1.5 group touch-manipulation"
                  style={{ minHeight: 44, minWidth: 36 }}
                  aria-label="Comment"
                >
                  <span className="group-hover:text-blue-500 group-active:scale-90 transition-all duration-100">
                    <IconComment />
                  </span>
                  {fmt(displayPost.commentsCount) && (
                    <span className="text-xs group-hover:text-blue-500 tabular-nums leading-none">
                      {fmt(displayPost.commentsCount)}
                    </span>
                  )}
                </button>

                {/* REPOST */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setRepostMenu({ open: true, post });
                  }}
                  className="flex items-center gap-1.5 group touch-manipulation"
                  style={{ minHeight: 44, minWidth: 36 }}
                  aria-label="Repost"
                >
                  <span className="group-hover:text-green-500 group-active:scale-90 transition-all duration-100">
                    <IconRepost />
                  </span>
                  {fmt(displayPost.repostsCount) && (
                    <span className="text-xs group-hover:text-green-500 tabular-nums leading-none">
                      {fmt(displayPost.repostsCount)}
                    </span>
                  )}
                </button>

                {/* LIKE */}
                <button
                  onClick={() => onLikePost({ postId: displayPost.postId, chatId: myChatId })}
                  className="flex items-center gap-1.5 group touch-manipulation"
                  style={{ minHeight: 44, minWidth: 36 }}
                  aria-label="Like"
                >
                  <span
                    className={`group-active:scale-90 transition-all duration-100 ${
                      displayPost.likedByMe ? "text-red-500" : "group-hover:text-red-500"
                    }`}
                  >
                    <IconLike filled={displayPost.likedByMe} />
                  </span>
                  {fmt(displayPost.likesCount) && (
                    <span
                      className={`text-xs tabular-nums leading-none ${
                        displayPost.likedByMe ? "text-red-500" : "group-hover:text-red-500"
                      }`}
                    >
                      {fmt(displayPost.likesCount)}
                    </span>
                  )}
                </button>

                {/* VIEWS */}
                <div
                  className="flex items-center gap-1.5 select-none"
                  style={{ minHeight: 44 }}
                  aria-label={`${displayPost.viewsCount || 0} views`}
                >
                  <IconViews />
                  <span className="text-xs tabular-nums leading-none">
                    {fmt(displayPost.viewsCount) || "0"}
                  </span>
                </div>

                {/* SUPPORT / TIP — send for others' posts, earnings view for own posts */}
                {showSupport && (
                  <button
                    type="button"
                    onClick={handleSupport}
                    className="flex items-center gap-1.5 group touch-manipulation"
                    style={{ minHeight: 44, minWidth: 36 }}
                    aria-label={isAuthor ? "View earnings" : "Support with USWD"}
                    title={isAuthor ? "View earnings" : "Support with USWD"}
                  >
                    <span className="group-hover:text-yellow-500 group-active:scale-90 transition-all duration-100">
                      <IconSupport />
                    </span>
                    {fmt(displayPost.supportsCount) && (
                      <span className="text-xs tabular-nums leading-none group-hover:text-yellow-500">
                        {fmt(displayPost.supportsCount)}
                      </span>
                    )}
                  </button>
                )}
              </div>

              {/* SHARE */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setMenu?.({ open: true, post: displayPost, type: "share" });
                }}
                className="flex items-center justify-center hover:text-blue-500 active:scale-90 transition-all duration-100 touch-manipulation"
                style={{ minHeight: 44, minWidth: 36 }}
                aria-label="Share"
              >
                <IconShare />
              </button>
            </div>
          </div>
        </div>
      </div>

      {zoomModal}
    </>
  );
}